/**
 * Verwaltung: Partnerzugaenge (Physios) und deren Einschulungen
 * Martin legt den Physio mit Name und E-Mail an, die Zugangsdaten gehen per Mail raus.
 */

import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { partnerAdminAPI } from '../../services/api';
import './Partner.css';

const PartnerVerwaltung = ({ showMessage }) => {
  const [partner, setPartner] = useState([]);
  const [einschulungen, setEinschulungen] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [neu, setNeu] = useState({ name: '', email: '' });
  const [sendet, setSendet] = useState(false);
  const [nurKommende, setNurKommende] = useState(true);

  const melde = (text, typ = 'success') => (showMessage ? showMessage(text, typ) : window.alert(text));

  const laden = async () => {
    try {
      const [p, e] = await Promise.all([partnerAdminAPI.getAll(), partnerAdminAPI.getEinschulungen()]);
      setPartner(p.data);
      setEinschulungen(e.data);
    } catch (err) {
      melde(err.response?.data?.error || 'Partnerdaten konnten nicht geladen werden', 'error');
    }
    setLaedt(false);
  };

  useEffect(() => { laden(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const anlegen = async (e) => {
    e.preventDefault();
    setSendet(true);
    try {
      const res = await partnerAdminAPI.create(neu);
      melde(res.data.mailGesendet
        ? `Zugang für ${res.data.partner.name} angelegt, Zugangsdaten per Mail verschickt.`
        : `Zugang angelegt, aber die Mail ging nicht raus. Bitte „Zugangsdaten neu senden“ nutzen.`,
      res.data.mailGesendet ? 'success' : 'error');
      setNeu({ name: '', email: '' });
      laden();
    } catch (err) {
      melde(err.response?.data?.error || 'Anlegen hat nicht geklappt', 'error');
    }
    setSendet(false);
  };

  const zugangSenden = async (p) => {
    if (!window.confirm(`Neues Passwort für ${p.name} erzeugen und an ${p.email} schicken? Das alte Passwort gilt dann nicht mehr.`)) return;
    try {
      await partnerAdminAPI.zugangSenden(p.id);
      melde(`Neue Zugangsdaten an ${p.email} verschickt.`);
    } catch (err) {
      melde(err.response?.data?.error || 'Senden hat nicht geklappt', 'error');
    }
  };

  const statusWechseln = async (p) => {
    const ziel = p.status === 'active' ? 'inactive' : 'active';
    if (ziel === 'inactive' && !window.confirm(`Zugang von ${p.name} sperren? Gebuchte Einschulungen bleiben bestehen.`)) return;
    try {
      await partnerAdminAPI.update(p.id, { status: ziel });
      laden();
    } catch (err) {
      melde(err.response?.data?.error || 'Ändern hat nicht geklappt', 'error');
    }
  };

  const heute = format(new Date(), 'yyyy-MM-dd');
  const sichtbar = nurKommende
    ? einschulungen.filter(e => e.datum >= heute).sort((a, b) => (a.datum + a.start).localeCompare(b.datum + b.start))
    : einschulungen;

  if (laedt) return <div className="loading">Lade...</div>;

  return (
    <div className="partner-verwaltung">
      <h2>Partner (Physios)</h2>
      <p className="pt-muted">
        Physios buchen im Partnerbereich Einschulungen für ihre Patient:innen. Jede Buchung landet als Anfrage
        unter „Anfragen“ (zuständig: Martin). Die Nuki-Berechtigung für den Physio wird weiterhin in der Nuki-App vergeben.
      </p>

      <div className="pt-card">
        <h3 className="pt-abschnitt">Neuen Partner anlegen</h3>
        <form onSubmit={anlegen} className="pt-form pt-inline-form">
          <label>
            Name
            <input value={neu.name} onChange={e => setNeu({ ...neu, name: e.target.value })} placeholder="z. B. Physio Muster" required />
          </label>
          <label>
            E-Mail
            <input type="email" value={neu.email} onChange={e => setNeu({ ...neu, email: e.target.value })} placeholder="praxis@email.at" required />
          </label>
          <button type="submit" className="pt-btn pt-btn-primaer" disabled={sendet}>
            {sendet ? 'Legt an…' : 'Anlegen & Zugang mailen'}
          </button>
        </form>
      </div>

      <div className="pt-card pt-tabelle-wrap">
        <table className="pt-tabelle">
          <thead>
            <tr><th>Name</th><th>E-Mail</th><th>Einschulungen</th><th>Status</th><th aria-label="Aktionen" /></tr>
          </thead>
          <tbody>
            {partner.length === 0 && (
              <tr><td colSpan="5" className="pt-muted">Noch keine Partner angelegt.</td></tr>
            )}
            {partner.map(p => (
              <tr key={p.id} className={p.status !== 'active' ? 'storniert' : ''}>
                <td data-label="Name"><strong>{p.name}</strong></td>
                <td data-label="E-Mail" className="pt-umbruch">{p.email}</td>
                <td data-label="Einschulungen">{p.einschulungen}</td>
                <td data-label="Status">
                  <span className={`pt-status ${p.status === 'active' ? 'mitglied' : 'aus'}`}>{p.status === 'active' ? 'Aktiv' : 'Gesperrt'}</span>
                </td>
                <td className="pt-zelle-aktion">
                  <button type="button" className="pt-link-btn" onClick={() => zugangSenden(p)}>Zugangsdaten neu senden</button>
                  <button type="button" className="pt-link-btn" onClick={() => statusWechseln(p)}>{p.status === 'active' ? 'Sperren' : 'Freischalten'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pt-titelzeile">
        <h3 className="pt-abschnitt">Einschulungen</h3>
        <label className="pt-checkbox pt-checkbox-klein">
          <input type="checkbox" checked={nurKommende} onChange={e => setNurKommende(e.target.checked)} />
          <span>nur kommende</span>
        </label>
      </div>
      <div className="pt-card pt-tabelle-wrap">
        <table className="pt-tabelle">
          <thead>
            <tr><th>Datum</th><th>Studio</th><th>Physio</th><th>Einschulung durch</th><th>Kunde</th><th>Status</th></tr>
          </thead>
          <tbody>
            {sichtbar.length === 0 && (
              <tr><td colSpan="6" className="pt-muted">Keine Einschulungen.</td></tr>
            )}
            {sichtbar.map(e => (
              <tr key={e.id} className={e.storniert ? 'storniert' : ''}>
                <td data-label="Datum">
                  <strong>{format(parseISO(e.datum), 'EEE, d.M.yyyy', { locale: de })}</strong>
                  <br /><span className="pt-muted">{e.start}–{e.ende} Uhr</span>
                </td>
                <td data-label="Studio">{e.studio}</td>
                <td data-label="Physio">{e.partner}</td>
                <td data-label="Einschulung durch">
                  {e.einschulungDurch === 'franz'
                    ? <span className="pt-status arbeit">FRANZ</span>
                    : <span className="pt-status aus">Physio</span>}
                </td>
                <td data-label="Kunde">{e.kunde}<br /><a href={`mailto:${e.email}`} className="pt-muted">{e.email}</a></td>
                <td data-label="Status"><span className="pt-status neu">{e.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PartnerVerwaltung;
